import {
  ActiveRequest,
  ClosedRequest,
  EsclationReason,
  EsclationStatusResponse,
  GetActiveTrackStatusDto,
  Status,
  Ticket,
} from '../types/services.type';
import { AxiosResponse } from 'axios';
import {
  GET_ESCALATION_REASONS_URL,
  GET_ESCLATION_STATUS_INFO_URL,
  GET_TICKET_STATUS_URL,
  REQUEST_ESCALATION_URL,
} from '../utils/environment';
import { getFormattedDate } from '../helpers/serviceHelpers';
import axiosInstance from '../utils/axiosInstance';

class TrackStatusService {
  static async getStatusDetailsByTicketNoAsync(
    ticketNumber: string | number,
    token: string,
    rcn: string | undefined | null,
  ): Promise<Ticket | null> {
    if (!ticketNumber) {
      throw new Error('ticketNumber is required');
    }

    if (!rcn) {
      throw new Error('rcn is required');
    }

    const data = {
      TicketNo: ticketNumber,
      RCN: rcn,
    };

    try {
      const response: AxiosResponse = await axiosInstance.post(GET_TICKET_STATUS_URL, data, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const closeRequest: Ticket[] = response?.data;

      if (!closeRequest || !closeRequest.length) {
        return null;
      }

      return closeRequest[0];
    } catch (error) {
      console.error('Error in getStatusDetailsByTicketNoAsync: ', error);
      return null;
    }
  }

  static async getAllActiveStatusAsync(RCN: string, token: string): Promise<ActiveRequest[]> {
    const {
      Open,
      Assigned,
      RequestForReAllocation,
      RequestCancellation,
      Pending,
      ResolvedSoftClosure,
      NegativeResponseFromCustomer,
    } = Status;

    let data: GetActiveTrackStatusDto = {
      RCN: RCN,
      StatusDetails: [
        Open,
        Assigned,
        RequestForReAllocation,
        RequestCancellation,
        Pending,
        ResolvedSoftClosure,
        NegativeResponseFromCustomer,
      ],
    };

    try {
      const response: AxiosResponse<ActiveRequest[]> = await axiosInstance.post(
        GET_TICKET_STATUS_URL,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      if (!response || !response.data) {
        console.error('Error in getAllActiveStatusAsync: Received invalid response from server');
        return [];
      }

      let activeRequests: ActiveRequest[] = response.data;
      return activeRequests.map((d) => {
        if (!d) {
          console.error('Error in getAllActiveStatusAsync: Received null or undefined ticket');
          return {};
        }
        return {
          ...d,
          CallBookDate: getFormattedDate(d.callBookDate),
        };
      }) as ActiveRequest[];
    } catch (error) {
      console.error('Error in getAllActiveStatusAsync: ', error);
      return [];
    }
  }

  static async getAllClosedStatusAsync(RCN: string, token: string): Promise<ClosedRequest[]> {
    let data = {
      RCN,
      StatusDetails: [Status.Closed],
    };

    try {
      if (!RCN) {
        throw new Error('RCN is null or undefined');
      }
      const response: AxiosResponse = await axiosInstance.post(GET_TICKET_STATUS_URL, data, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response || !response.data) {
        console.error('Error in getAllActiveStatusAsync: Received invalid response from server');
        return [];
      }

      let closeRequest: ClosedRequest[] = response.data;
      if (!closeRequest) {
        console.error('Error in getAllActiveStatusAsync: Received null or undefined closedRequest');
        return [];
      }

      return closeRequest
        .sort((a, b) => new Date(b.callBookDate).getTime() - new Date(a.callBookDate).getTime())
        .map((d) => {
          if (!d) {
            console.error('Error in getAllActiveStatusAsync: Received null or undefined ticket');
            return {};
          }
          return {
            ...d,
            callBookDate: getFormattedDate(d.callBookDate),
          };
        }) as ClosedRequest[];
    } catch (error) {
      console.error('Error in getAllActiveStatusAsync: ', error);
      return [];
    }
  }

  static async getEsclationReasonsAsync(token: string): Promise<EsclationReason[]> {
    try {
      const response: AxiosResponse<EsclationReason[]> = await axiosInstance.get(
        GET_ESCALATION_REASONS_URL,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      if (!response || !response.data) {
        console.error('Error in getEsclationReasonsAsync: Received invalid response from server');
        return [];
      }

      return response.data;
    } catch (error) {
      console.error('Error in getEsclationReasonsAsync: ', error);
      return [];
    }
  }

  static async requestEscalationAsync(
    ticketNo: string,
    esclationReasonCode: string,
    token: string,
  ): Promise<{ status: boolean; message: string }> {
    try {
      if (!ticketNo || !esclationReasonCode) {
        throw new Error('requestEscalationAsync: ticketNo and esclationReasonCode are required');
      }

      const data = {
        EscalationRequestId: 0,
        TicketNo: ticketNo,
        EscalationId: esclationReasonCode,
        source: 'MS',
      };

      const response: AxiosResponse = await axiosInstance.post(REQUEST_ESCALATION_URL, data, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response || !response.data) {
        throw new Error('requestEscalationAsync: Received invalid response from server');
      }

      const esclationReason: { status: boolean; message: string } = response.data;

      if (!esclationReason || !esclationReason.status || !esclationReason.message) {
        throw new Error('requestEscalationAsync: Invalid response from server');
      }

      return esclationReason;
    } catch (error) {
      console.error('Error in requestEscalationAsync: ', error);
      return {
        status: false,
        message: 'Something went wrong',
      };
    }
  }

  static async getEscalationStatusAsync(
    ticketNo: string,
    token: string,
  ): Promise<EsclationStatusResponse> {
    try {
      if (!ticketNo) {
        throw new Error('getEscalationStatusAsync: ticketNo is required');
      }

      const data = {};

      console.log(`${GET_ESCLATION_STATUS_INFO_URL}/${ticketNo}`);

      const response: AxiosResponse = await axiosInstance.post(
        `${GET_ESCLATION_STATUS_INFO_URL}/${ticketNo}`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      if (!response || !response.data) {
        throw new Error('getEscalationStatusAsync: Received invalid response from server');
      }

      const esclationStatus = response.data;

      if (!esclationStatus.status || !esclationStatus.data) {
        throw new Error('getEscalationStatusAsync: Invalid response from server');
      }

      return esclationStatus;
    } catch (error) {
      console.error('Error in getEscalationStatusAsync: ', error);
      return {
        status: 'false',
        data: {
          ref_no: '',
          escalated_reasons: '',
          escalated_date: '',
        },
      };
    }
  }
}

export default TrackStatusService;
